import { ReactElement, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { T } from "react-translator-component";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../redux/store";
import appServices from "../../services/app-services/appServices";
import { isSubLeaderRole, isGroupLeader, isPrivateGroup, convertUserImage, findBlockData } from "../../util/utils";
import { createNewPostState, getCreatePostType } from "../../util/CreatePost"
import { Avatar, Box, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import GCBackButton from "../../components/GreenCity/GCBackButton";
import TextButton from "../../components/GreenCity/TextButton";
import PostCard from "../../components/lists/PostCard";
import MebukuIdUpgradeBanner from "../../components/MebukuIdUpgradeBanner";
import LoadingModal from "../../components/LoadingModal";
import SeeMoreText from "../../components/SeeMoreText";
import BlockModal from "../../components/BlockModal";
import LoadMoreButton from "../../components/LoadMoreButton";

const AddIcon = () => {
  return (
    <div style={styles.addIcon}>
      <AddRoundedIcon sx={{ width: 20, height: 20 }} />
    </div>
  );
};

function ViewGroupPost(): ReactElement {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const state = useLocation().state;
  const groupInfo = useSelector((state: any) => state.gcGroup);
  const { user } = useSelector((state: any) => state.userAuth);
  const { blocks } = useSelector((state: any) => state.blocks);
  const [groupPost, setGroupPost] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<any>({});
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [blockId, setBlockId] = useState<number | null>(null);
  const [showBockModal, setShowBockModal] = useState<boolean>(false);

  useEffect(() => {
    if (state.post_type === "user" && user.id !== state?.user.id) updateBlockState()
  }, [blocks])

  const apiCall = (page: number) => {
    if (!page) return

    if (state.post_type === "user") {
      getUserPost(page);
    } else {
      getGroupPosts(page);
    }
  }

  useEffect(() => {
    apiCall(1)
    // eslint-disable-next-line
  }, []);

  const updateBlockState = () => {
    const blockData = findBlockData(blocks, state?.user.id)
    setIsBlocked(blockData ? true : false)
    setBlockId(blockData?.id ?? null)
  }

  const getGroupPosts = async (page: number) => {
    setLoading(true);
    try {
      const res = await appServices.getGroupPosts(
        groupInfo?.id,
        state?.post_type !== "public",
        state?.post_type === "private_users",
        page
      );

      setGroupPost((pre: any[]) => [...pre, ...res.posts]);
      setPagination(res.pagination);
    } catch {
      // pass
    } finally {
      setLoading(false);
    }
  };

  const getUserPost = async (page: number) => {
    if (!state?.user?.id) return

    setLoading(true);
    try {
      const res = await appServices.getUserPosts(
        state.user.id,
        state.user.class_name,
        page
      );
      setGroupPost((pre: any[]) => [...pre, ...res.posts]);
      setPagination(res.pagination);
    } catch {
      // pass
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    let title = "";
    if (state.post_type === "public") {
      title = `${T("everyone_post")}`;
    } else if (state.post_type === "private") {
      title = `${T("inside_group_post")}`;
    } else if (state.post_type === "private_users") {
      title = `${T("group_private_post")}`;
    }

    return title;
  };

  const getDescroptionElementWidth = () => {
    const element: any = document.querySelector("#user-descroption")
    if (element) return element.offsetWidth
  }

  const showAddPostIcon = () => {
    return state?.post_type !== "user" &&
      (isSubLeaderRole(groupInfo?.role) || isGroupLeader(groupInfo, user)) &&
      // 非公開グループではみんなの投稿はできない
      !(isPrivateGroup(groupInfo?.groupIsPublic) && state.post_type === "public")
  }

  return (
    <div style={styles.container}>
      <LoadingModal visible={loading} />
      <div style={{ marginLeft: 10 }}>
        <GCBackButton onClick={() => navigate(-1)} />
      </div>

      <Box component="div" style={styles.topButtonContainer}>
        {state?.post_type === "user" ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Avatar
              src={convertUserImage(state?.user?.image)}
              sx={{
                height: 50,
                width: 50,
                marginRight: "10px",
              }}
            />
            <Typography style={styles.userName}>{state.user.nickname}</Typography>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Typography style={styles.everyonePostStyle}>{getTitle()}</Typography>
          </div>
        )}

        {isBlocked && (
          <div style={styles.blockedStatusBtn} onClick={() => setShowBockModal(true)}>
            {T("is_blocking")}
          </div>
        )}

        {showAddPostIcon() && (
          <TextButton
            onClick={() => navigate("/createPost", createNewPostState(getCreatePostType(state?.post_type), user, groupInfo, [], dispatch))}
            Icon={AddIcon}
            title={T("submit_post")}
          />
        )}
      </Box>
      {(state?.post_type === "user" && state.user.description) && (
        <div style={styles.userDescription} id="user-descroption">
          <SeeMoreText text={state.user.description} lineHeight={25} width={getDescroptionElementWidth()}/>
        </div>
      )}

      {groupPost?.map((item: any, index: number) => (
        <PostCard
          post={item}
          key={index}
        />
      ))}

      <LoadMoreButton show={pagination?.next_page > 0} onClick={() => apiCall(pagination?.next_page)} />

      {isBlocked && blockId && (
        <>
          <BlockModal
            open={showBockModal}
            currentUserId={user.id}
            isGroupPost={state.post_type !== "user"}
            blockId={blockId}
            targetId={state.post_type === "user" ? state.user.id : groupInfo.id}
            onContinue={() => setShowBockModal(false)}
            onCancel={() => setShowBockModal(false)}
            onClose={() => setShowBockModal(false)}
          />
        </>
      )}
      <MebukuIdUpgradeBanner />
    </div>
  );
}

export default ViewGroupPost;

const styles = {
  container: {
    paddingTop: 90,
  },
  addIcon: {
    border: "2px solid",
    borderRadius: 5,
    borderColor: "#000000",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  topButtonContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 20,
    padding: "0 20px",
  },

  everyonePostStyle: {
    fontSize: 16,
  },

  userName: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: "50px",
  },

  userDescription: {
    fontSize: 16,
    margin: "10px auto 30px",
    lineHeight: "25px",
    letterSpacing: 0,
    width: 'calc(100% - 40px)',
    wordBreak: "break-all" as 'break-all'
  },

  blockedStatusBtn: {
    width: "100px",
    height: "40px",
    fontSize: 14,
    textAlign: 'center' as 'center',
    lineHeight: "34px",
    border: "3px solid orange",
    borderRadius: "5px",
    backgroundColor: "rgba(252, 199, 198, .5)"
  },
};
